import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import dl from 'downloadjs';
import { datasetApi, getDatasetRequestConfig } from '../api';
import { errorBodySchema, toUserMessage } from '../getErrorMessage';

export const FORMATS = ['csv', 'json', 'xlsx'] as const;
export type DownloadFormat = (typeof FORMATS)[number];

const mimeTypes: Record<DownloadFormat, string> = {
  json: 'application/json',
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export class DatasetExportError extends Error {}

// The request asks for a blob, so error bodies arrive as a Blob as well and
// have to be read and parsed before their message can be shown.
const toExportError = async (error: unknown): Promise<unknown> => {
  if (!isAxiosError(error) || !(error.response?.data instanceof Blob)) {
    return error;
  }

  try {
    const parsed = errorBodySchema.safeParse(
      JSON.parse(await error.response.data.text())
    );
    return parsed.success
      ? new DatasetExportError(toUserMessage(parsed.data.message))
      : error;
  } catch {
    return error;
  }
};

export const useExportDataset = () => {
  return useMutation({
    mutationFn: async ({
      dataset,
      format,
    }: {
      dataset: string;
      format: DownloadFormat;
    }) => {
      const config = getDatasetRequestConfig();

      const response = await datasetApi
        .exportDatasetApiV2DatasetsDatasetNameDataGet(
          dataset,
          // MDH exports its own `_id` and `__digest_md5` alongside the dataset's
          // columns unless asked not to. They are meaningless in a downloaded
          // file, and re-uploading one that carries `_id` makes MDH rebuild the
          // whole collection instead of applying a delta.
          { exclude_internal_fields: true },
          {
            ...config,
            headers: {
              ...config.headers,
              Accept: mimeTypes[format],
            },
            responseType: 'blob',
          }
        )
        .catch(async (error: unknown) => {
          throw await toExportError(error);
        });

      if (!(response.data instanceof Blob)) {
        throw new Error('Expected Blob response from export endpoint');
      }

      return response.data;
    },
    onSuccess: (data, { dataset, format }) => {
      dl(data, `${dataset}.${format}`, mimeTypes[format]);
    },
  });
};
